import { IExpert } from '@/shared/IExpert';
import { ApiService } from './ApiService';
import { StorageCacheService } from './StorageCacheService';
import { UserInfoService } from './UserInfoService';
import { INVITE_TYPE } from '@/shared/constants';
import { IUserInfo } from '@/shared/IUserInfo';

export class CourseExpertStorage {

  static async getExpert(id: string, force?: boolean): Promise<IExpert | undefined> {
    const cached = StorageCacheService.getValue<IExpert>(`expert/${id}`);
    if (!force && cached) {
      return cached;
    }

    const result = await ApiService.fetch<IExpert>(`/api/expert/${id}`);
    if (result) {
      StorageCacheService.setValue(`expert/${id}`, result);
      return result;
    }
  }

  static async createExpert(expert: IExpert, invite: boolean): Promise<IExpert> {

    const { ensured, invited } = await UserInfoService.ensureUser(expert, invite ? INVITE_TYPE.EXPERT : INVITE_TYPE.NONE);
    
    const result = await ApiService.fetch<IExpert>(`/api/expert`, 'POST', {
      ...expert,
      email: ensured.email,
    });

    if (invite && !invited) {
      await CourseExpertStorage.inviteExpert(result.id);
    }

    StorageCacheService.resetCache(`expert`);
    StorageCacheService.setValue(`expert/${result.id}`, result);
    return result;
  }

  static async inviteExpert(id: string): Promise<IExpert> {
    const result = await ApiService.fetch<IExpert>(`/api/expert/${id}/invite`, 'POST');
    return result;
  }

  static async patchExpert(expert: IExpert, invite: boolean): Promise<IExpert> {

    const { ensured, invited } = await UserInfoService.ensureUser(expert, invite ? INVITE_TYPE.EXPERT : INVITE_TYPE.NONE);

    const result = await ApiService.fetch<IExpert>(`/api/expert/${expert.id}`, 'PATCH', {
      ...expert,
      email: ensured.email,
    });

    if (invite && !invited) {
      await CourseExpertStorage.inviteExpert(result.id);
    }
    
    StorageCacheService.resetCache(`expert`);
    StorageCacheService.setValue(`expert/${result.id}`, result);
    return result;
  }

  static async deleteExpert(id: string): Promise<IExpert> {
    const result = await ApiService.fetch<IExpert>(`/api/expert/${id}`, 'DELETE');
    StorageCacheService.resetCache(`expert`);
    return result;
  }

  static async getExperts(force?: boolean): Promise<IExpert[]> {
    if (!force) {
      const result = StorageCacheService.getValue<IExpert[]>('expert');
      if (result) {
        return result;
      }
    }

    const items = await ApiService.fetch<IExpert[]>(`/api/expert`);

    StorageCacheService.resetCache(`expert`);
    StorageCacheService.setValue(`expert`, items);
    for (const item of items) {
      StorageCacheService.setValue(`expert/${item.id}`, item);
    }

    return items;
  }

  static async getStaff(force?: boolean): Promise<IUserInfo[]> {
    if (!force) {
      const result = StorageCacheService.getValue<IUserInfo[]>('staff');
      if (result) {
        return result;
      }
    }

    const items = await ApiService.fetch<IUserInfo[]>(`/api/staff`);

    StorageCacheService.resetCache(`staff`);
    StorageCacheService.setValue(`staff`, items);
    for (const item of items) {
      StorageCacheService.setValue(`staff/${item.id}`, item);
    }

    return items;
  }

}